import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faBan,
	faChevronUp,
	faDatabase,
	faDownload,
	faEnvelope,
	faPhone,
	faPlus,
	faRadar,
	faSparkles,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import useLocalStorageState from "use-local-storage-state";

import { Icon } from "@/components";
import { creditMenuSubtitle } from "@/components/CreditConfirmModal";
import { formatCappedCount } from "@/utils/formatCappedCount";
import { Spinner } from "@/components/Spinner/Spinner";
import { TablePagination } from "@/components/TablePagination/TablePagination";
import { CrmProvider } from "@/pages/Private/redux/crm/crm.schema";
import { useConnectedCrmProviders } from "@/pages/Private/pages/Integrations/hooks/useConnectedCrmProviders";
import { crmAddToMenuEntries } from "@/pages/Private/components/AddToCrm/crmAddToMenuEntries";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import { SelectListPopover } from "./SelectListPopover";

type ActionType = "addTo" | "enrich" | "crm" | null;

interface ContactsActionBarProps {
	selectedCount: number;
	overallCount?: number;
	/** `overallCount` is the server's count cap, not a total — render it as "N+". */
	isCountCapped?: boolean;
	columnsCount: number;
	columnsShown: number;
	isAddToListLoading: boolean;
	isExportLoading: boolean;
	isEnrichEmailLoading: boolean;
	isEnrichPhoneLoading: boolean;
	isAddToTrackerLoading: boolean;
	isAddToBlacklistLoading: boolean;
	isAddToCrmLoading?: boolean;
	onAddToList: (list: { id: number; name: string }) => void;
	onCreateAndAddToList: (name: string) => void;
	onAddToCrm?: (provider: CrmProvider) => void;
	onEnrichEmail: () => void;
	onEnrichPhone: () => void;
	onExport: () => void;
	onAddToTracker: () => void;
	onAddToBlacklist: () => void;
	currentPage?: number;
	totalPages?: number;
	handleNavigateToPage?: (page: number) => void;
	handleChangeRowsPerPage?: (rowsPerPage: number) => void;
	limit?: number;
	clearSelection?: () => void;
}

export const ContactsActionBar: FunctionComponent<ContactsActionBarProps> = ({
	selectedCount,
	overallCount,
	isCountCapped,
	isAddToListLoading,
	isExportLoading,
	isEnrichEmailLoading,
	isEnrichPhoneLoading,
	isAddToTrackerLoading,
	isAddToBlacklistLoading,
	isAddToCrmLoading,
	onAddToList,
	onCreateAndAddToList,
	onAddToCrm,
	onEnrichEmail,
	onEnrichPhone,
	onExport,
	onAddToTracker,
	onAddToBlacklist,
	currentPage,
	totalPages,
	handleNavigateToPage,
	handleChangeRowsPerPage,
	limit,
	clearSelection,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.${key}`), [t]);

	const connectedCrmProviders = useConnectedCrmProviders();
	const crmMenuEntries = crmAddToMenuEntries(connectedCrmProviders, t);

	const [openPopover, setOpenPopover] = useState<ActionType>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	const [isSidebarOpen] = useLocalStorageState("isSidebarOpen", {
		defaultValue: false,
	});

	const togglePopover = (type: ActionType) => {
		setOpenPopover((prev) => (prev === type ? null : type));
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (popoverRef.current && !composedPathContains(popoverRef.current, e)) {
				setOpenPopover(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			const footerElement = document.querySelector("#contacts-action-bar") as HTMLElement;
			const floatingBarElement = document.getElementById("contacts-floating-bulk-bar");
			const outletElement = document.getElementById("outlet-container");

			if (outletElement && footerElement) {
				footerElement.style.setProperty("width", `${outletElement.offsetWidth}px`, "important");
			}

			if (outletElement && floatingBarElement) {
				const rect = outletElement.getBoundingClientRect();
				const center = rect.left + rect.width / 2;

				floatingBarElement.style.setProperty("left", `${center}px`, "important");
				floatingBarElement.style.setProperty("max-width", `${rect.width - 16}px`, "important");
			}
		};

		handleResize();

		const outletElement = document.getElementById("outlet-container");

		if (outletElement) {
			resizeObserverRef.current = new ResizeObserver(handleResize);
			resizeObserverRef.current.observe(outletElement);
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			resizeObserverRef.current?.disconnect();
		};
	}, [selectedCount, isSidebarOpen]);

	return (
		<>
			{selectedCount > 0 && (
				<div
					className="fixed bottom-[54px] left-1/2 -translate-x-1/2 z-150 flex flex-row items-center gap-2 bg-gray-900 text-white rounded-lg pl-4 pr-2 py-2 shadow-lg"
					id="contacts-floating-bulk-bar"
				>
					<span className="flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
						<span className="bg-blue-action text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
							{selectedCount}
						</span>
						selected
					</span>

					{clearSelection && (
						<button
							aria-label="Clear selection"
							className="flex items-center justify-center w-5 h-5 rounded text-white hover:bg-white/10"
							type="button"
							onClick={clearSelection}
						>
							<Icon className="w-3.5 h-3.5" icon={faXmark} />
						</button>
					)}

					<span className="w-px h-5 bg-white/20 mx-1" />

					<div ref={popoverRef} className="flex flex-row gap-1 items-center">
						{/* Add to */}
						<div className="relative">
							{isAddToListLoading ? (
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
									<Spinner color="#fff" />
									{t("basics.wait")}
								</div>
							) : (
								<button
									className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
									type="button"
									onClick={() => togglePopover("addTo")}
								>
									<Icon className="w-3.5 h-3.5" icon={faPlus} />
									{ts("addTo")}
									<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
								</button>
							)}
							{openPopover === "addTo" && (
								<div className="absolute bottom-full mb-2 left-0">
									<SelectListPopover
										isLoading={isAddToListLoading}
										onCreateNew={(name) => {
											onCreateAndAddToList(name);
											setOpenPopover(null);
										}}
										onSelectList={(list) => {
											onAddToList(list);
											setOpenPopover(null);
										}}
									/>
								</div>
							)}
						</div>

						{/* Add to CRM */}
						{onAddToCrm && crmMenuEntries.length > 0 && (
							<div className="relative">
								{isAddToCrmLoading ? (
									<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
										<Spinner color="#fff" />
										{t("basics.wait")}
									</div>
								) : (
									<button
										className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
										type="button"
										onClick={() => togglePopover("crm")}
									>
										<Icon className="w-3.5 h-3.5" icon={faDatabase} />
										{t("basics.addToCrm")}
										<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
									</button>
								)}
								{openPopover === "crm" && (
									<div className="absolute bottom-full mb-2 left-0 w-[190px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[2000]">
										{crmMenuEntries.map(({ provider, label }) => (
											<button
												key={provider}
												className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer duration-150 flex items-center gap-2"
												type="button"
												onClick={() => {
													onAddToCrm(provider);
													setOpenPopover(null);
												}}
											>
												<Icon className="w-4 text-gray-700" icon={faDatabase} />
												{label}
											</button>
										))}
									</div>
								)}
							</div>
						)}

						{/* Enrich */}
						<div className="relative">
							{isEnrichEmailLoading || isEnrichPhoneLoading ? (
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
									<Spinner color="#fff" />
									{t("basics.wait")}
								</div>
							) : (
								<button
									className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
									type="button"
									onClick={() => togglePopover("enrich")}
								>
									<Icon className="w-3.5 h-3.5" icon={faSparkles} />
									{ts("enrich")}
									<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
								</button>
							)}
							{openPopover === "enrich" && (
								<div className="absolute bottom-full mb-2 left-0 w-[180px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[2000]">
									<button
										className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer duration-150 flex items-center gap-2"
										type="button"
										onClick={() => {
											onEnrichEmail();
											setOpenPopover(null);
										}}
									>
										<Icon className="w-4 text-gray-700" icon={faEnvelope} />
										<span className="flex flex-col">
											{ts("searchForEmail")}
											<span className="text-xs text-text-meta">
												{creditMenuSubtitle(t, "email")}
											</span>
										</span>
									</button>
									<button
										className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer duration-150 flex items-center gap-2"
										type="button"
										onClick={() => {
											onEnrichPhone();
											setOpenPopover(null);
										}}
									>
										<Icon className="w-4 text-gray-700" icon={faPhone} />
										<span className="flex flex-col">
											{ts("searchForPhone")}
											<span className="text-xs text-text-meta">
												{creditMenuSubtitle(t, "phone")}
											</span>
										</span>
									</button>
								</div>
							)}
						</div>

						{/* Add to Tracker */}
						{isAddToTrackerLoading ? (
							<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
								<Spinner color="#fff" />
								{t("basics.wait")}
							</div>
						) : (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
								type="button"
								onClick={onAddToTracker}
							>
								<Icon className="w-3.5 h-3.5" icon={faRadar} />
								{ts("addToTracker")}
							</button>
						)}

						{/* Add to Blacklist */}
						{isAddToBlacklistLoading ? (
							<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
								<Spinner color="#fff" />
								{t("basics.wait")}
							</div>
						) : (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
								type="button"
								onClick={onAddToBlacklist}
							>
								<Icon className="w-3.5 h-3.5" icon={faBan} />
								{ts("addToBlacklist")}
							</button>
						)}

						{/* Export */}
						{isExportLoading ? (
							<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
								<Spinner color="#fff" />
								{t("basics.wait")}
							</div>
						) : (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
								type="button"
								onClick={onExport}
							>
								<Icon className="w-3.5 h-3.5" icon={faDownload} />
								{t("basics.export")}
							</button>
						)}
					</div>
				</div>
			)}

			{/* Slim fixed footer */}
			<div
				className="fixed flex flex-row items-center w-auto h-[38px] border-t border-t-border bottom-0 right-0 z-150 bg-white overflow-hidden"
				id="contacts-action-bar"
			>
				<div className="absolute inset-0 flex flex-row items-center px-5 gap-3 text-xs text-text-meta">
					<button
						className="flex items-center gap-1 text-text-meta hover:text-blue-action font-medium cursor-pointer"
						disabled={isExportLoading}
						type="button"
						onClick={onExport}
					>
						<Icon className="w-3.5 h-3.5" icon={faDownload} />
						{t("basics.export")}
					</button>
					<span className="w-px h-4 bg-border-light" />
					{overallCount ? (
						<>
							<span className="whitespace-nowrap">
								{t("basics.rows")}:{" "}
								<span className="text-text">{formatCappedCount(overallCount, isCountCapped)}</span>
							</span>
							<span className="w-px h-4 bg-border-light" />
						</>
					) : null}
					<div className="flex-1" />
					{handleNavigateToPage && (
						<TablePagination
							currentPage={currentPage ?? 1}
							handleChangeRowsPerPage={handleChangeRowsPerPage}
							handleNavigateToPage={handleNavigateToPage}
							limit={limit ?? 15}
							maxPage={totalPages ?? 1}
						/>
					)}
				</div>
			</div>
		</>
	);
};
